/**
 * @module ContainerTriageHelpers
 * @purpose Pure functions for the triage panel: already-ingested detection,
 *   group select-all state, and error-code mapping for the selective-run
 *   submit call. Kind filtering is server-side (see
 *   use-container-triage-inventory) -- not duplicated here.
 * @depends types/evidence-containers, services/api (ApiError)
 * @dependedOnBy use-container-triage sub-hooks, container-triage.parts
 */

import { ApiError } from '../../../services/api'
import type { ContainerInventoryItem, InventoryGroup } from '../../../types/evidence-containers'

export const ENTRY_ALREADY_INGESTED_CODE = 'ENTRY_ALREADY_INGESTED'
export const RUN_ALREADY_ACTIVE_CODE = 'RUN_ALREADY_ACTIVE'

/**
 * @behaviour An item counts as already-ingested once a run has recorded rows
 *   for it, regardless of the current `needsReingest` flag.
 */
export function isAlreadyIngested(item: ContainerInventoryItem): boolean {
  return item.lastIngestionRunId !== null
}

export type GroupCheckState = 'all' | 'some' | 'none'

/**
 * @behaviour Drives the group select-all checkbox's checked/indeterminate state.
 */
export function groupCheckState(
  group: InventoryGroup,
  selectedKeys: ReadonlySet<string>
): GroupCheckState {
  if (group.items.length === 0) return 'none'
  const selectedCount = group.items.filter((item) => selectedKeys.has(item.itemKey)).length
  if (selectedCount === 0) return 'none'
  if (selectedCount === group.items.length) return 'all'
  return 'some'
}

export interface SubmitErrorMapping {
  code: string | null
  message: string
}

/**
 * @behaviour Reads the backend's `{error, message}` envelope off an ApiError
 *   (both ENTRY_ALREADY_INGESTED and RUN_ALREADY_ACTIVE ship a fully
 *   humanised `message` already -- safe to surface verbatim).
 * @errors Falls back to a generic Error message, or a fixed string for
 *   anything else, so the caller always has text to show.
 */
export function mapSubmitError(err: unknown): SubmitErrorMapping {
  if (err instanceof ApiError) {
    const code = err.body?.error ?? null
    return { code, message: err.body?.message ?? err.message }
  }
  if (err instanceof Error) return { code: null, message: err.message }
  return { code: null, message: 'Unable to start the ingestion run' }
}
